import React from 'react';

interface Column { label: string; key: string; isCurrency?: boolean; align?: 'left' | 'center' | 'right'; }

interface Props {
  columns: Column[];
  rows: Record<string, any>[];
  showTotals?: boolean;
  totalsLabel?: string;
  sectionTitle?: string;
  colorScheme?: string;
}

const THEME: Record<string, { headerBg: string; headerText: string; accent: string; totalBg: string; altBg: string }> = {
  default:   { headerBg: 'bg-[#1a5276]', headerText: 'text-white', accent: 'border-[#d4af37]', totalBg: 'bg-emerald-50 dark:bg-emerald-900/20', altBg: 'bg-gray-50 dark:bg-gray-800/30' },
  grayscale: { headerBg: 'bg-[#2c3e50]', headerText: 'text-white', accent: 'border-[#95a5a6]', totalBg: 'bg-gray-100 dark:bg-gray-800/40', altBg: 'bg-gray-50 dark:bg-gray-800/20' },
  green:     { headerBg: 'bg-[#1e8449]', headerText: 'text-white', accent: 'border-[#d4af37]', totalBg: 'bg-green-50 dark:bg-green-900/20', altBg: 'bg-green-50/40 dark:bg-green-900/10' },
  blue:      { headerBg: 'bg-[#1f3a5f]', headerText: 'text-white', accent: 'border-[#d4af37]', totalBg: 'bg-blue-50 dark:bg-blue-900/20', altBg: 'bg-blue-50/30 dark:bg-blue-900/10' },
};

const fmt = (v: number) => `Rs. ${v.toLocaleString('en-PK', { minimumFractionDigits: 0 })}`;

const ReportTable: React.FC<Props> = ({ columns, rows, showTotals, totalsLabel = 'GRAND TOTAL', sectionTitle, colorScheme = 'default' }) => {
  const t = THEME[colorScheme] || THEME.default;

  const totals = showTotals ? columns.map((col, i) => {
    if (i === 0) return totalsLabel;
    if (!col.isCurrency) return '';
    const sum = rows.reduce((s, r) => s + (typeof r[col.key] === 'number' ? r[col.key] : 0), 0);
    return fmt(sum);
  }) : null;

  return (
    <div className="my-4">
      {sectionTitle && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg border-l-4 ${t.accent} bg-gray-50 dark:bg-gray-800/50`}>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{sectionTitle}</span>
        </div>
      )}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-b-lg shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className={t.headerBg}>
              {columns.map((col, i) => (
                <th key={i} className={`px-3 py-2.5 ${t.headerText} font-semibold text-xs tracking-wider uppercase whitespace-nowrap
                  ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx} className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-colors
                ${rIdx % 2 === 0 ? t.altBg : ''}`}>
                {columns.map((col, cIdx) => {
                  const val = row[col.key];
                  return (
                    <td key={cIdx} className={`px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300
                      ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                      ${col.isCurrency ? 'font-mono tabular-nums' : ''}`}>
                      {col.isCurrency && typeof val === 'number' ? fmt(val) : (val ?? '—')}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">No data found for the selected filters</td></tr>
            )}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr className={`${t.totalBg} font-bold border-t-2 border-gray-300 dark:border-gray-600`}>
                {totals.map((val, i) => (
                  <td key={i} className={`px-3 py-2.5 text-gray-800 dark:text-gray-200
                    ${columns[i]?.align === 'right' ? 'text-right' : columns[i]?.align === 'center' ? 'text-center' : 'text-left'}
                    ${columns[i]?.isCurrency ? 'font-mono tabular-nums' : ''}`}>
                    {val}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default ReportTable;
